"use client";

import { useEffect, useRef } from "react";
import { useAuthStore } from "@/modules/auth/stores/useAuthStore";
import { useCheckoutStore } from "../stores/useCheckoutStore";

/**
 * Apaga o rascunho do checkout quando ele não é (mais) de quem está no
 * navegador. Mesmo formato do `AuthInit`/`SessionInit`: monte uma vez no
 * root layout.
 *
 * Por que é obrigatório: o rascunho agora sobrevive ao reload (sessionStorage) e
 * carrega o ENDEREÇO COMPLETO e o telefone do recebedor. Numa máquina
 * compartilhada, sem isto, quem entrasse depois na mesma aba veria — e poderia
 * fechar um pedido para — o endereço da pessoa anterior. Duas condições limpam:
 *
 * - **sem sessão** (logout, ou visitante): o rascunho não tem dono, então cai.
 *   Como o logout sempre precede o próximo login, isto já cobre a troca de
 *   pessoa, inclusive com reload no meio;
 * - **troca de titular** dentro da mesma montagem, por garantia.
 *
 * Fica no root layout de propósito: se estivesse só nas telas de checkout, o
 * logout numa outra rota passaria batido.
 */
export function CheckoutInit() {
    const status = useAuthStore((state) => state.status);
    const cpfCnpj = useAuthStore((state) => state.user?.cpfCnpj);
    // `undefined` = primeira passada (reload autenticado mantém o rascunho).
    const owner = useRef<string | null | undefined>(undefined);

    useEffect(() => {
        if (status === "loading") return;

        const next = status === "authenticated" ? (cpfCnpj ?? null) : null;
        const previous = owner.current;
        owner.current = next;

        // Sem sessão (logout ou visitante): o rascunho não tem dono, cai. Troca
        // de titular também. Mas visitante que ACABOU de entrar (previous null)
        // não perde nada: fazer login no meio do checkout não pode apagar o que
        // a pessoa já digitou — e não há dado de outra pessoa envolvido.
        if (next === null || (typeof previous === "string" && previous !== next)) {
            useCheckoutStore.getState().reset();
        }
    }, [status, cpfCnpj]);

    return null;
}
